/**
 * HomeworkPage.jsx — Student
 * DeutschBridge Institute
 *
 * Three submission modes: typed answer, file upload, TikTok URL.
 * Any combination is accepted (at least one required).
 * Handles already-submitted state with grade/feedback display.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// ── Brand tokens ─────────────────────────────────────────────
const R = '#DD0000';
const B = '#1A1A1A';
const G = '#FFCE00';

// ── Helpers ───────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}
function isOverdue(due) {
  if (!due) return false;
  return new Date(due) < new Date();
}
function extractTikTokId(url) {
  const m = url.match(/tiktok\.com\/@[\w.]+\/video\/(\d+)/);
  return m ? m[1] : null;
}
function LevelBadge({ level }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 10px', borderRadius: 20,
      background: R, color: '#fff',
      fontSize: 11, fontWeight: 800, letterSpacing: '0.5px',
    }}>
      {level}
    </span>
  );
}

// ── TikTok embed ──────────────────────────────────────────────
function TikTokPreview({ url }) {
  const [html, setHtml] = useState(null);
  useEffect(() => {
    if (!url || !extractTikTokId(url)) return;
    setHtml(null);
    fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`)
      .then(r => r.json())
      .then(d => setHtml(d.html || null))
      .catch(() => setHtml(null));
  }, [url]);
  if (!html) return (
    <div style={{ padding: '10px 0' }}>
      <a href={url} target="_blank" rel="noopener noreferrer"
        style={{ color: R, fontSize: 13, wordBreak: 'break-all' }}>
        🔗 {url}
      </a>
    </div>
  );
  return <div dangerouslySetInnerHTML={{ __html: html }} style={{ marginTop: 8 }} />;
}

// ── Submitted summary badge ───────────────────────────────────
function SubmittedBadge({ sub }) {
  const graded = sub.score != null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 12px', borderRadius: 20,
      background: graded ? '#F0FDF4' : '#FFFBEB',
      border: `1px solid ${graded ? '#86EFAC' : '#FCD34D'}`,
      color:   graded ? '#15803D'  : '#92400E',
      fontSize: 12, fontWeight: 700,
    }}>
      {graded ? '✓ Graded' : '⏳ Pending Review'}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════
// AssignmentCard
// ═════════════════════════════════════════════════════════════
function AssignmentCard({ assignment, onSubmitted }) {
  const { user } = useAuth();
  const { addToast } = useToast();

  const sub = assignment.mySubmission;

  const [answerText, setAnswerText]   = useState('');
  const [tiktokUrl, setTiktokUrl]     = useState('');
  const [tiktokValid, setTiktokValid] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // { file, name, size }
  const [deletingFile, setDeletingFile] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(!!sub);
  const [localSub, setLocalSub]       = useState(sub);

  const fileInputRef = useRef(null);
  const isExpanded = !submitted;

  const hasContent = answerText.trim() || tiktokUrl.trim() || selectedFile;

  const handleTiktokChange = (v) => {
    setTiktokUrl(v);
    setTiktokValid(!!extractTikTokId(v));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(pdf|doc|docx)$/i)) {
      addToast('Only PDF, .doc, or .docx files are accepted.', 'error');
      return;
    }
    setSelectedFile({ file, name: file.name, size: file.size });
  };

  const handleDeleteFile = async () => {
    if (!localSub?.sub_id || !localSub?.file_url) {
      // File not yet uploaded — just clear local state
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setDeletingFile(true);
    try {
      await API.delete(`/homework/submissions/${localSub.sub_id}/file`);
      setLocalSub(p => ({ ...p, file_url: null, file_name: null }));
      setSelectedFile(null);
      addToast('File removed.', 'success');
    } catch (e) {
      addToast(e.response?.data?.error || 'Failed to remove file.', 'error');
    } finally {
      setDeletingFile(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasContent) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (answerText.trim()) fd.append('answer_text', answerText.trim());
      if (tiktokUrl.trim())  fd.append('tiktok_url', tiktokUrl.trim());
      if (selectedFile)      fd.append('file', selectedFile.file);

      const { data } = await API.post(
        `/homework/assignments/${assignment.id}/submit`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setLocalSub({
        sub_id:          data.submissionId,
        answer_text:     answerText.trim() || null,
        tiktok_url:      tiktokUrl.trim() || null,
        file_name:       selectedFile?.name || null,
        submission_type: [answerText && 'text', selectedFile && 'file', tiktokUrl && 'tiktok'].filter(Boolean).join('+'),
        submitted_at:    new Date().toISOString(),
        score:           null,
        feedback:        null,
      });
      setSubmitted(true);
      addToast('Homework submitted! 🎉', 'success');
      onSubmitted?.();
    } catch (e) {
      addToast(e.response?.data?.error || e.message || 'Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const overdue = isOverdue(assignment.due_date);

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      marginBottom: 20,
    }}>
      {/* Card header */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <LevelBadge level={assignment.level} />
              {assignment.cohort_name && (
                <span style={{ fontSize: 11, color: '#6B7280', background: '#F3F4F6', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                  🏫 {assignment.cohort_name}
                </span>
              )}
              {localSub && <SubmittedBadge sub={localSub} />}
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: B, lineHeight: 1.3 }}>
              {assignment.title}
            </h3>
            {assignment.description && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                {assignment.description}
              </p>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', gap: 20, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: overdue ? R : '#6B7280', fontWeight: overdue ? 700 : 500 }}>
            {overdue ? '⚠️ ' : '📅 '}Due: {fmtDate(assignment.due_date)}
          </span>
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            🏆 Max: {assignment.max_score} pts
          </span>
        </div>
      </div>

      {/* Teacher file attachment */}
      {(assignment.file_url || assignment.file_name) ? (
        <div style={{ margin: '14px 20px 0', padding: '14px 16px', background: B, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28, flexShrink: 0 }}>📄</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Assignment File
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {assignment.file_name || 'attachment'}
            </p>
          </div>
          <a href={assignment.file_url} target="_blank" rel="noopener noreferrer"
            style={{ flexShrink: 0, padding: '8px 14px', background: R, color: '#fff', borderRadius: 7, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
            ⬇ Download
          </a>
        </div>
      ) : (
        <div style={{ margin: '14px 20px 0', padding: '12px 16px', background: '#F9FAFB', borderRadius: 10, border: '1px dashed #D1D5DB' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#9CA3AF' }}>📎 No file attached by teacher</p>
        </div>
      )}

      {/* ── Submitted state ── */}
      {submitted && localSub ? (
        <div style={{ padding: '18px 20px' }}>

          {/* Submission summary */}
          <div style={{ padding: '14px 16px', background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0', marginBottom: 16 }}>
            <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ✅ Your Submission — {fmtDate(localSub.submitted_at)}
            </p>
            {localSub.answer_text && (
              <div style={{ marginBottom: 8 }}>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: '#6B7280', fontWeight: 700 }}>TYPED ANSWER</p>
                <p style={{ margin: 0, fontSize: 13, color: B, lineHeight: 1.55, maxHeight: 80, overflow: 'hidden' }}>
                  {localSub.answer_text.slice(0, 200)}{localSub.answer_text.length > 200 ? '…' : ''}
                </p>
              </div>
            )}
            {localSub.file_name && (
              <div style={{ marginBottom: 8 }}>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: '#6B7280', fontWeight: 700 }}>UPLOADED FILE</p>
                <p style={{ margin: 0, fontSize: 13, color: B, fontWeight: 600 }}>📄 {localSub.file_name}</p>
              </div>
            )}
            {localSub.tiktok_url && (
              <div>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: '#6B7280', fontWeight: 700 }}>TIKTOK LINK</p>
                <a href={localSub.tiktok_url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 13, color: R, wordBreak: 'break-all' }}>
                  🎵 {localSub.tiktok_url}
                </a>
              </div>
            )}
          </div>

          {/* Grade / feedback */}
          {localSub.score != null ? (
            <div style={{ padding: '14px 16px', background: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: localSub.feedback ? 10 : 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1E40AF' }}>📊 Your Grade</p>
                <span style={{ fontSize: 22, fontWeight: 900, color: R, fontFamily: 'Syne, sans-serif' }}>
                  {localSub.score}<span style={{ fontSize: 14, fontWeight: 500, color: '#6B7280' }}>/{assignment.max_score}</span>
                </span>
              </div>
              {localSub.feedback && (
                <p style={{ margin: 0, fontSize: 13, color: '#1E3A8A', lineHeight: 1.6, paddingTop: 8, borderTop: '1px solid #BFDBFE' }}>
                  💬 {localSub.feedback}
                </p>
              )}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: '#92400E', background: '#FFFBEB', padding: '10px 14px', borderRadius: 8, border: '1px solid #FCD34D' }}>
              ⏳ Your submission is awaiting review from your teacher.
            </p>
          )}

          {/* Allow resubmission if not yet graded */}
          {localSub.score == null && (
            <button onClick={() => setSubmitted(false)} style={{
              marginTop: 12, background: 'none', border: `1px solid ${R}`, color: R,
              borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              ✏️ Edit Submission
            </button>
          )}
        </div>
      ) : (
        /* ── Submission form ── */
        <div style={{ padding: '18px 20px' }}>

          <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, color: B }}>
            Submit your homework (use any combination below):
          </p>

          {/* Option 1: Typed answer */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: B, marginBottom: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: R, color: '#fff', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>1</span>
              Type Your Answer
            </label>
            <textarea
              value={answerText}
              onChange={e => setAnswerText(e.target.value)}
              placeholder="Type your homework answer here..."
              rows={5}
              style={{
                width: '100%', minHeight: 150, padding: '12px 14px',
                border: `2px solid ${answerText ? R : '#E5E7EB'}`,
                borderRadius: 10, fontSize: 14, lineHeight: 1.6,
                resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                fontFamily: 'inherit', transition: 'border-color 0.2s',
              }}
              onFocus={e  => e.target.style.borderColor = R}
              onBlur={e   => e.target.style.borderColor = answerText ? R : '#E5E7EB'}
            />
            {answerText && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
                {wordCount(answerText)} words · {answerText.length} characters
              </p>
            )}
          </div>

          {/* Option 2: File upload */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: B, marginBottom: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: R, color: '#fff', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>2</span>
              Upload a File
              <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF', marginLeft: 4 }}>(PDF, DOC, DOCX)</span>
            </label>

            {selectedFile ? (
              /* File selected — show info box */
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: B, borderRadius: 10 }}>
                <div style={{ fontSize: 26, flexShrink: 0 }}>📄</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile.name}</p>
                  <p style={{ margin: '1px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{fmtSize(selectedFile.size)}</p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    style={{ padding: '6px 12px', background: R, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Delete
                  </button>
                  <button onClick={() => fileInputRef.current?.click()}
                    style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed #D1D5DB', borderRadius: 10,
                  padding: '20px', textAlign: 'center', cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = R}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#D1D5DB'}
              >
                <div style={{ fontSize: 28, marginBottom: 6 }}>📁</div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#374151' }}>Click to upload a file</p>
                <p style={{ margin: '3px 0 0', fontSize: 11, color: '#9CA3AF' }}>PDF, DOC, or DOCX only</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* Option 3: TikTok link */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: B, marginBottom: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: R, color: '#fff', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>3</span>
              TikTok Video Link
            </label>
            <input
              type="url"
              value={tiktokUrl}
              onChange={e => handleTiktokChange(e.target.value)}
              placeholder="https://www.tiktok.com/@username/video/..."
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10,
                border: `2px solid ${tiktokUrl ? (tiktokValid ? '#22C55E' : R) : '#E5E7EB'}`,
                fontSize: 14, outline: 'none', boxSizing: 'border-box',
                fontFamily: 'inherit', transition: 'border-color 0.2s',
              }}
            />
            {tiktokUrl && !tiktokValid && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: R }}>Paste a valid TikTok video URL to see a preview.</p>
            )}
            {tiktokValid && <TikTokPreview url={tiktokUrl} />}
          </div>

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!hasContent || submitting}
            style={{
              width: '100%', padding: '13px', borderRadius: 10, border: 'none',
              background: hasContent && !submitting ? R : '#D1D5DB',
              color: hasContent && !submitting ? '#fff' : '#9CA3AF',
              fontSize: 14, fontWeight: 700, cursor: hasContent && !submitting ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background 0.2s',
            }}
          >
            {submitting ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'db-spin 0.8s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="40 20" />
                </svg>
                Submitting…
              </>
            ) : '🚀 Submit Homework'}
          </button>

          {!hasContent && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
              Add at least one of: typed answer, file, or TikTok link
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// Main page
// ═════════════════════════════════════════════════════════════
export default function HomeworkPage() {
  const { addToast } = useToast();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading]         = useState(true);

  const load = () => {
    setLoading(true);
    API.get('/homework/assignments')
      .then(({ data }) => setAssignments(data.assignments || []))
      .catch(() => addToast('Failed to load assignments', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px', fontFamily: 'Space Grotesk, sans-serif' }}>
      <style>{`@keyframes db-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }`}</style>

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: B }}>Homework</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6B7280' }}>
          Complete and submit your assignments below.
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spinner size="lg" className="text-brand-500" />
        </div>
      ) : assignments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: B }}>No assignments yet</p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#9CA3AF' }}>Your teacher hasn't assigned any homework yet.</p>
        </div>
      ) : (
        assignments.map(a => (
          <AssignmentCard key={a.id} assignment={a} onSubmitted={load} />
        ))
      )}
    </div>
  );
}
